import React, {useEffect, useState} from 'react';

import {Box, Button, Grid, Typography} from "@mui/material";
import Bottom from "../Bottom";
import Top from "../Top";
import HistoryLoadState from "../../components/HistoryLoadState";
import Circle from "../../components/CircleProgress";
import BarChart from "../../components/BarChart";
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import {styled} from '@mui/material/styles';
import schedule from "../../assets/icon/schedule.gif";
import * as util from "../../utils/util";
import api from "../../utils/api";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import HistorySliderInput from "../../components/HistorySliderInput";
import {useNavigate} from "react-router-dom";
import { notifyApiError } from "../../utils/apiError";

const params = {
  token: '',
  from: '',
  to: '',
  target: ''
}

const initTargetStep = {
  step: 0,
  target_step: 0
}

const HistoryWalking = () => {
  const navigate = useNavigate();
  const [targetStep, setTargetStep] = React.useState(initTargetStep);
  const [series, setSeries] = React.useState([
    { dataKey: 'step', label: '걸음수', valueFormatter: (value: number | null) => `${value}mm`, stack: 'total' }
  ]);
  const [dataset, setDataset] = React.useState([{ target: 0, step: 0, month: '' }]);
  const [dayValue, setDayValue] = useState(0);
  /**
   * 화면 상단 날짜. 서버 응답이 아니라 dayValue 에서 바로 만든다.
   *
   * 예전에는 빈 문자열로 시작해 조회 성공(result_code === '0') 안에서만 채웠다.
   * 그래서 (1) 응답이 올 때까지 '..' 만 보였고 (2) 조회가 실패하면 영영 '..' 로 남았다.
   * 날짜는 클라이언트가 이미 아는 값이라 서버 응답에 묶어 둘 이유가 없다.
   */
  const targetDate = util.getDateByCustom('yyyyMMdd', dayValue);
  const [loading, setLoading] = React.useState(true);
  const [failed, setFailed] = React.useState(false);
  const [reloadNonce, setReloadNonce] = React.useState(0);


  useEffect(() => {
    setStepData();
  }, [dayValue, reloadNonce]);

  function setStepData() {
    setLoading(true);
    setFailed(false);
    // 걸음수 정보 조회
    params.token = localStorage.getItem('token') || '';
    params.from = util.getDateByCustom('yyyyMMdd', dayValue - 7);
    params.to = util.getDateByCustom('yyyyMMdd', dayValue);
    params.target = util.getDateByCustom('yyyyMMdd', dayValue);

    api.get(`app/api/v1/AP_002_005?token=${params.token}&from=${params.from}&to=${params.to}&target=${params.target}`).then(res => {
      if (res.data.result_code === '0') {
        setTargetStep(res.data.result_data.target);
        setSeries(series);
        let tempArr = [];
        for (let item of res.data.result_data.data.step_v2) {
          let obj = { target: 0, step: 0, month: '' };
          obj.month = item.date;
          obj.step = item.step;
          tempArr.push(obj);
        }
        setDataset(tempArr.reverse());
      } else {
        // result_code 가 '0' 이 아니면 조회 실패다. 예전에는 아무 처리도 없어서
        // 수치는 0 인 채로, 날짜는 '..' 로 굳고 사용자에게는 아무 안내도 없었다.
        console.warn('[기록] 조회 실패 result_code=', res.data.result_code, res.data.result_message);
        setFailed(true);
      }
    }).catch(function (error) {
      console.log(error);
      setFailed(true);
    }).finally(function () {
      setLoading(false);
    });
  }

  // 목표 걸음수 저장
  function saveTargetStep(value: any) {
    const saveParams = { date: '', targetStep: '' };
    saveParams.date = targetDate.slice(0, 4) + '-' + targetDate.slice(4, 6) + '-' + targetDate.slice(6, 8);
    saveParams.targetStep = value;

    api.post(`app/api/v1/AP_002_012`, saveParams).then(res => {
      setStepData();
    }).catch(function (error) {
      // 저장 실패를 알리지 않으면 사용자는 기록이 남은 줄 안다
      notifyApiError(error, '목표 걸음수를 저장하지 못했습니다.');
    });
  }



  return (
    <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
      <Top title="걸음 기록" />

      <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%', minHeight: 500 }}>
        <Box>
          <Typography component="div" sx={{ fontSize: 20, color: '#555', m: 4, mb: 0, textAlign: 'center', fontWeight: 700 }}>
            <ArrowLeftIcon onClick={() => setDayValue((prev) => prev - 1)} sx={{ verticalAlign: 'text-bottom' }} />
            {targetDate.slice(0, 4) + '.' + targetDate.slice(4, 6) + '.' + targetDate.slice(6, 8)}
            <ArrowRightIcon onClick={() => setDayValue((prev) => prev + 1)} sx={{ 'display': dayValue < 0 ? 'inline-block' : 'none', verticalAlign: 'text-bottom' }} />
          </Typography>

          {/* 조회 중 / 실패 표시 — 날짜 바로 아래 한 줄 */}
          <HistoryLoadState loading={loading} failed={failed} onRetry={() => setReloadNonce((n) => n + 1)} />

          <Stack direction={'row'} justifyContent={'center'} sx={{ m: '20px 0', p: 1, bgcolor: '#fff', borderRadius: '15px', boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)' }}>
            <Grid sx={{
              '& .CircleChart .MuiGauge-root': { width: '130px', height: '130px' },
              '& .CircleChart .circleText': { fontSize: 24, },
              '& .MuiGauge-valueArc': {
                fill: '#e029ff !important',
              },
            }}>
              <Circle progress={{ goal: targetStep.target_step, value: targetStep.step }} />
            </Grid>

            <Stack justifyContent={'center'} sx={{ m: 'auto', }}>
              <Avatar alt="" src={schedule} sx={{ m: 'auto', width: '60px', height: '60px' }} />
            </Stack>

            <Stack direction={'row'} alignItems={'center'} sx={{ ml: 'auto', mr: 2 }}>
              <Stack direction={'column'} sx={{ ml: 'auto' }}>
                <Typography component="div" sx={{ fontSize: 35, color: '#e029ff', fontWeight: 700 }}>
                  {util.formatComma(targetStep.step)}
                </Typography>
                <Typography component="span" sx={{ fontSize: 20, color: '#999', mb: 0, textAlign: 'right' }}>
                  {util.formatComma(targetStep.target_step)}
                </Typography>
              </Stack>

              <Typography component="div" sx={{ fontSize: 18, color: '#777', ml: 1, mb: 2 }}>
                Step
              </Typography>
            </Stack>
          </Stack>

          <Grid container justifyContent="center" alignItems="center" >
            <HistorySliderInput info={{ value: targetStep.target_step, title: '목표 걸음수를 입력하세요', unit: 'step', btnTitle: '목표 설정', max: 20000 }} handleComplete={saveTargetStep} />
          </Grid>

          <Box sx={{ bgcolor: '#fff', p: '0 10px 10px 10px', borderRadius: '15px', boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)' }}>
            <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{ p: 1 }}>
              <Stack >
                <Typography component="div" sx={{ fontSize: 14, color: '#555', }}>최근 7일</Typography>
              </Stack>
              <Stack sx={{ ml: 'auto', zIndex: 2  }} >
                <Button  sx={{fontSize:12, color:'#555', }} endIcon={<ArrowRightIcon />}
                  onClick={() => { navigate('../history/MonthHistory', {state: {type: 'step'}}) }}>
                월간현황
              </Button>
              </Stack>
            </Stack>
            <Divider />
            <Box sx={{
              mt: -3.5,
              '& .MuiBarElement-root': { fill: '#e029ff' },
              '& .MuiChartsLegend-mark': { fill: '#e029ff' }
            }}>
              <BarChart dataset={dataset}
                chartSetting={chartSet}
                series={series}
              />
            </Box>
          </Box>
        </Box>
      </Grid>

      <Bottom />
    </Stack>
  );
}

const EditButton = styled(Button)({
  boxShadow: 'none',
  textTransform: 'none',
  fontSize: 16,
  fontWeight: 700,
  padding: '6px 20px',
  lineHeight: 1.5,
  backgroundColor: '#fff',
  border: '1px solid #EF8322',
  borderRadius: '20px',
  color: '#EF8322',
  margin: '10px 0 30px 0',
  width: '47%',
  '&:hover': {
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
    boxShadow: 'none',
    color: '#fff'
  },
  '&:active': {
    boxShadow: 'none',
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
  },
  '&:focus': {
    boxShadow: 'none',
  },
});

const chartSet = {
  xAxis: [
    {
      label: '',
    },
  ],
  maxWidth: 450,
  height: 300,
  slotProps: {
    legend: {
      direction: 'row',
      position: { vertical: 'bottom', horizontal: 'middle' },
      padding: -5,
      itemMarkWidth: 10,
      itemMarkHeight: 10,
      labelStyle: {
        fontSize: 12,
      },
    },
  },
};

export default HistoryWalking;